import { Component, forwardRef } from '@angular/core';
import {
  SelectItemV2,
  findItemInList,
  ToggleV2Module,
  InputV2Module,
  SelectV2Module,
  IconV2Module,
} from '@schenck/rotec-components';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  NG_VALIDATORS,
  UntypedFormControl,
  ValidationErrors,
} from '@angular/forms';
import { AlarmModel, AlarmOffsetUnit } from 'src/api';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';

@Component({
  selector: 'ma-scheduling-alarm',
  templateUrl: './scheduling-alarm.component.html',
  styleUrls: ['./scheduling-alarm.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SchedulingAlarmComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SchedulingAlarmComponent),
      multi: true,
    },
  ],
  imports: [
    TranslatePipe,
    TranslateDirective,
    ToggleV2Module,
    InputV2Module,
    SelectV2Module,
    IconV2Module,
  ],
})
export class SchedulingAlarmComponent implements ControlValueAccessor {
  public formControl: UntypedFormControl;
  public model: AlarmModel;

  public valueChange = (alarmModel: AlarmModel) => {};
  public touched = () => {};

  public alarmIntervalItems: SelectItemV2<AlarmOffsetUnit>[] = [
    { value: AlarmOffsetUnit.Days, name: 'Day(s)' },
    { value: AlarmOffsetUnit.Weeks, name: 'Week(s)' },
    { value: AlarmOffsetUnit.Months, name: 'Month(s)' },
  ];

  public get isAlarmActive(): boolean {
    return !!this.model;
  }

  public set isAlarmActive(active: boolean) {
    if (active) {
      this.model = {
        additionalRecipients: [],
        offsetUnit: AlarmOffsetUnit.Weeks,
        offsetValue: 1,
      };
    } else {
      this.model = null;
    }
    this.valueChange(this.model);
  }

  public get additionalEmails(): string {
    return this.model.additionalRecipients.join(';');
  }

  public set additionalEmails(str: string) {
    // if the string is not empty, the user has touched the input
    // we want to set the touched state after 3 seconds to give the user time to enter the email addresses
    if (str?.length > 0) {
      setTimeout(() => this.touched(), 3000);
    }

    const separators = [';', ','];
    this.model.additionalRecipients = str
      .split(new RegExp(separators.join('|'), 'g'))
      .map((email) => email.trim());
    this.valueChange(this.model);
  }

  public get offsetValue(): number {
    return this.model.offsetValue;
  }

  public set offsetValue(value: number) {
    this.model.offsetValue = value;
    this.valueChange(this.model);
  }

  public get offsetUnit(): SelectItemV2<AlarmOffsetUnit> {
    return findItemInList(this.alarmIntervalItems, this.model.offsetUnit);
  }

  public set offsetUnit(value: SelectItemV2<AlarmOffsetUnit>) {
    this.model.offsetUnit = value.value;
    this.valueChange(this.model);
  }

  // ==================================== form methods
  public writeValue(obj: any): void {
    this.model = obj;
  }

  public registerOnChange(fn: any): void {
    this.valueChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.touched = fn;
  }

  public validate(control: UntypedFormControl): ValidationErrors | null {
    this.formControl = control;
    const value = control.value;
    const emails = value?.additionalRecipients.map((email) => email.trim());

    const invalidEmails =
      typeof emails !== 'undefined' &&
      emails.filter(
        (email) =>
          email.trim() === '' ||
          !/^[\W]*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4}[\W]*;{1}[\W]*)*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4})[\W]*$/i.test(
            email
          )
      );
    const noRecipientsMsg =
      !!invalidEmails && invalidEmails.filter((email) => email !== '').length > 0
        ? 'Please enter valid email addresses.'
        : '';

    if (
      (!!value && value?.additionalRecipients?.length === 0) ||
      (!!invalidEmails && invalidEmails[0] === '') ||
      (!!invalidEmails && invalidEmails.length > 0)
    ) {
      return {
        noRecipients: noRecipientsMsg,
      };
    }
  }
}
